import React from 'react';
import { observer } from 'mobx-react';
import { ReviewDetailsWrapper } from '@seedlang/hoc';
import styled from '@emotion/styled';
import { DeckUI, AppUI, OverlayUI } from '@seedlang/state';
import { Constants, Theme } from '@seedlang/constants';
import { isPresent } from '@seedlang/utils';
import IntervalTester from 'components/card/interval_tester';
import InPlaceText from 'components/form/in_place_text';
import CardTypeSelect from 'components/review/card_type_select';
import CardNote from 'components/card/card_note';
import ReviewHistoryTable from 'components/review/review_history_table';
import Alert from 'components/alert';
import Modal from 'components/modal';
import Button from 'components/button/button';
import Spinner from 'components/spinner';
import Text from 'components/text';
import SegmentedTriangle from 'components/svg/segmented_triangle';
import { observable } from 'mobx';

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: ${props => props.inDeck ? '20px' : '10px'};
`;

const TwoColumnLayout = styled.div`
  display: flex;
  flex-direction: ${props => props.inDeck ? 'column' : 'row'};
  padding-bottom: ${props => props.inDeck ? '20px' : '0'};
  gap: ${props => props.inDeck ? '10px' : '20px'};
  width: 100%;
  align-items: stretch;

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 700px) {
    width: 100%;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  order: ${props => props.inDeck ? -1 : 0};

  @media (max-width: 700px) {
    order: 1;
    width: 100%;
  }
`;

const Module = styled.div`
  background: white;
  width: 100%;
  padding: ${props => props.inDeck ? '10px 10px 12px' : '10px'};
  margin-bottom: ${props => props.inDeck ? '0' : '10px'};
  border-radius: 5px;
  position: relative;
  flex: 1;
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 10px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid #f0f0f0;
`;

const Cell = styled.div`
  text-align: left;
  flex: ${props => props.flex};
  padding: 4px 0;
  font-size: 13px;
  color: #333;
`;

const LabelCell = styled(Cell)`
  font-weight: bold;
  color: #777;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex: 1;
`;

const NavList = styled.div`
  overflow: hidden;
  margin-top: 14px;
`;

const NavItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  border-bottom: ${props => props.last ? 'none' : '1px solid #ebebeb'};
  background: white;
  &:hover { background: #fafafa; }
`;

const Buttons = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 300px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`;


const TriangleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${props => props.inDeck ? '4px 0 6px' : '6px 0 12px'};
  max-width: 320px;
  margin-top: ${props => props.inDeck ? '8px' : '20px'};
  margin-left: auto;
  margin-right: auto;
`;

const InfoButton = styled.button`
  position: absolute;
  top: 8px;
  right: ${props => props.inDeck ? '20px' : '8px'};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #aaa;
  font-size: 15px;
  display: flex;
  align-items: center;
  &:hover { color: #777; }
`;

const StatusTag = styled.div`
  background: ${props => props.background};
  color: #FFF;
  border-radius: 8px;
  font-size: 12px;
  padding: 3px 8px;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Explainer = styled.div`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
  a {
    color: ${Theme.blue};
    text-decoration: underline;
  }
`;

const CardPreview = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid #ebebeb;
  overflow: hidden;
  margin-bottom: 14px;
`;

const CardPreviewRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  border-bottom: ${props => props.last ? 'none' : '1px solid #ebebeb'};
`;

const CardPreviewLabel = styled.div`
  font-size: 10px;
  font-weight: bold;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 3px;
  text-align: left;
`;

const CardPreviewValue = styled.div`
  font-size: 15px;
  color: #222;
  text-align: left;
`;

const SwapRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  border-top: 1px solid #ebebeb;
  &:hover {
    background: #fafafa;
  }
`;

function isCustomReview(card) {
  if (!card) return false;
  const customCardType = Constants.REVIEW_CARD_TYPES.find(item => item.slug === 'user_submission');
  return Boolean(card.userSubmission || card.cardType?.slug === 'user_submission' || (customCardType && String(card.cardTypeId) === String(customCardType.id)));
}

function reviewDetailFrontDisplay(card) {
  if (!card) return '';
  if (isCustomReview(card)) return card.frontText || '';
  if (card.word && !card.sentence) return card.word.targetTextWithDefiniteArticle || '';
  if (card.sentence) return card.sentence.targetText || '';
  return card.frontText || '';
}

function reviewDetailBackDisplay(card) {
  if (!card) return '';
  if (isCustomReview(card)) return card.backText || '';
  return card.reviewBackText();
}

@observer
class ReviewDetails extends React.Component {
  @observable showChangeCardTypeModal = false;
  @observable showMoveToDeckModal = false;
  @observable showEditNoteModal = false;
  @observable showExplainer = false;
  @observable reviewActionConfirm = null;

  openConfirm = (key) => {
    this.reviewActionConfirm = key;
  }

  closeConfirm = () => {
    this.reviewActionConfirm = null;
  }

  onConfirmReviewAction = () => {
    const k = this.reviewActionConfirm;
    this.reviewActionConfirm = null;
    if (k === 'reset') this.props.onResetReview();
    else if (k === 'snooze') this.props.onSnoozeReview();
    else if (k === 'retire') this.props.onRetireReview();
    else if (k === 'delete') this.props.onDeleteReview();
  }

  onMoveToDeck = (deckId) => {
    if (!this.props.card?.id || !deckId || String(deckId) === String(this.props.card.deckId)) return;
    if (typeof this.props.card.set === 'function') this.props.card.set('deckId', deckId);
    DeckUI.cardStore.batchUpdate(
      { data: { cardIds: [this.props.card.id], deckId } },
      () => AppUI.cardStore.getShow({ ids: { cardId: this.props.card.id } }),
    );
    this.showMoveToDeckModal = false;
  }

  reviewProgressFillForSegment(n) {
    const total = AppUI.user.daysUntilReviewRetired || 1;
    const interval = this.props.intervalTesterCard?.repetitionInterval || 0;
    if (interval === 0) return '#e0e0e0';
    if (n === 1) return Theme.green;
    const filled = Math.round(Math.min(interval / total, 1) * 10);
    return filled >= n ? Theme.green : '#e0e0e0';
  }

  render() {
    const inDeck = isPresent(this.props.overlayId);
    if (this.props.showSpinner) {
      return (
        <Wrapper inDeck={inDeck}>
          <Spinner />
        </Wrapper>
      );
    }
    if (this.props.card) {
      const reviewBackText = reviewDetailBackDisplay(this.props.card);
      return (
        <Wrapper inDeck={inDeck}>
          {
            this.reviewActionConfirm &&
            <Modal
              onCloseModal={this.closeConfirm}
              animate
              gradient
              width='360px'
              marginTop='300px'
              title={this.props.reviewActionConfirmTitle[this.reviewActionConfirm]}
            >
              <Text
                textAlign='center'
                padding='10px 20px'
              >
                {this.props.reviewActionConfirmMessage[this.reviewActionConfirm]}
              </Text>
              <div style={{display: 'flex', gap: '10px', justifyContent: 'center', padding: '10px 20px 20px'}}>
                <Button
                  onClick={this.onConfirmReviewAction}
                  background={this.reviewActionConfirm === 'delete' ? Theme.red : Theme.blue}
                  color='white'
                >
                  Confirm
                </Button>
                <Button
                  onClick={this.closeConfirm}
                >
                  Cancel
                </Button>
              </div>
            </Modal>
          }
          {
            this.showChangeCardTypeModal &&
            <Modal
              onCloseModal={() => this.showChangeCardTypeModal = false}
              animate
              width={DeckUI.layout.deckWidth - 40}
              marginTop='200px'
              marginLeft='40px'
              title='Select a Card Type'
              gradient
            >
              <div style={{padding: '10px 0 10px'}}>
                <CardTypeSelect
                  card={this.props.card}
                  onChangeCardTypeId={this.props.onChangeCardTypeId}
                />
              </div>
              {
                this.props.showUpdatedCardTypeMessage && this.props.card.cardTypeName &&
                <Alert
                  textAlign='center'
                  width='100%'
                  margin='10px 0 0 0'
                  background={Theme.green}
                >
                  {`Next time this card appears in a deck it will be a ${this.props.card.cardTypeName} card`}
                </Alert>
              }
            </Modal>
          }
          {
            this.showMoveToDeckModal &&
            <Modal
              onCloseModal={() => this.showMoveToDeckModal = false}
              animate
              width={DeckUI.layout.deckWidth - 40}
              marginTop='200px'
              marginLeft='40px'
              title='Move to Another Deck'
              gradient
            >
              <div style={{display: 'flex', flexDirection: 'column', gap: '10px', padding: '10px 0 10px'}}>
                {
                  AppUI.reviewDeckOptions.length === 0 &&
                  <Text textAlign='center' padding='10px 20px'>
                    There is no other review deck to move this card to.
                  </Text>
                }
                {
                  AppUI.reviewDeckOptions.map(([id, name]) =>
                    <Button
                      key={id}
                      borderRadius='5px'
                      background={String(id) === String(this.props.card.deckId) ? Theme.blue : '#ECECEC'}
                      color={String(id) === String(this.props.card.deckId) ? '#FFF' : '#333'}
                      onClick={() => this.onMoveToDeck(id)}
                    >
                      {name}
                    </Button>
                  )
                }
              </div>
            </Modal>
          }
          {
            this.showEditNoteModal &&
            <Modal
              onCloseModal={() => this.showEditNoteModal = false}
              animate
              width={DeckUI.layout.deckWidth - 40}
              marginTop='150px'
              marginLeft='40px'
              title='Review Note'
              gradient
            >
              <CardNote
                card={this.props.card}
                cardId={this.props.cardId}
              />
            </Modal>
          }
          {
            DeckUI.user.admin && this.props.card && this.props.card.id &&
            <IntervalTester
              card={this.props.intervalTesterCard}
              processScore={this.props.processScore}
              forceReviewAfterDue={this.props.forceReviewAfterDue}
              reviewsDueAtBeginningOfDay={this.props.intervalTesterReviewsDueAtBeginningOfDay}
              onToggleIntervalTesterReviewsDueAtBeginningOfDay={this.props.onToggleIntervalTesterReviewsDueAtBeginningOfDay}
            />
          }
          <TwoColumnLayout inDeck={inDeck}>
            <LeftColumn>
              <Module>
                {
                  Boolean(isPresent(reviewDetailFrontDisplay(this.props.card)) || isPresent(reviewBackText) || isCustomReview(this.props.card)) &&
                  <CardPreview>
                    {
                      isPresent(reviewDetailFrontDisplay(this.props.card)) &&
                      <CardPreviewRow last={!isPresent(reviewBackText) && !isCustomReview(this.props.card)}>
                        <CardPreviewLabel>Front</CardPreviewLabel>
                        {
                          isCustomReview(this.props.card) ?
                          <InPlaceText
                            allowUpdate
                            inputType='textarea'
                            model='cards'
                            field='front_text'
                            id={this.props.card.id}
                            defaultValue={reviewDetailFrontDisplay(this.props.card)}
                            textAlign='left'
                            onBlur={this.props.onUpdateFrontText}
                            afterChange={this.props.onUpdateFrontText}
                          />
                          :
                          <CardPreviewValue>{reviewDetailFrontDisplay(this.props.card)}</CardPreviewValue>
                        }
                      </CardPreviewRow>
                    }
                    {
                      isPresent(reviewBackText) &&
                      <CardPreviewRow last={!isCustomReview(this.props.card)}>
                        <CardPreviewLabel>Back</CardPreviewLabel>
                        {
                          isCustomReview(this.props.card) ?
                          <InPlaceText
                            allowUpdate
                            inputType='textarea'
                            model='cards'
                            field='back_text'
                            id={this.props.card.id}
                            defaultValue={reviewBackText}
                            textAlign='left'
                            onBlur={this.props.onUpdateBackText}
                            afterChange={this.props.onUpdateBackText}
                          />
                          :
                          <CardPreviewValue>{reviewBackText}</CardPreviewValue>
                        }
                      </CardPreviewRow>
                    }
                    {
                      isCustomReview(this.props.card) &&
                      <SwapRow onClick={this.props.onSwapFrontAndBack}>
                        <span>Swap front and back</span>
                        <i className='fa fa-exchange' style={{color: '#aaa', fontSize: '12px', transform: 'rotate(45deg)'}} />
                      </SwapRow>
                    }
                  </CardPreview>
                }
                {
                  this.props.card.cardType && this.props.card.userSubmission && this.props.card.word && isPresent(this.props.card.word?.target?.videoClip) &&
                  <Button
                    fontSize='13px'
                    margin='0 0 12px'
                    width='100%'
                    disabled={this.props.card.cardTypeId === Constants.REVIEW_CARD_TYPES.find(item => item.slug === 'user_submission').id}
                    onClick={this.props.onSwitchBackToCustom}
                    background={Theme.green}
                  >
                    Switch back to custom card
                  </Button>
                }
                <Table>
                  <Row>
                    <LabelCell flex='1'>Deck</LabelCell>
                    <Cell flex='1'>{AppUI.reviewDeckStore.indexData.find(d => d.id === this.props.card.deckId)?.name || '-'}</Cell>
                  </Row>
                  <Row>
                    <LabelCell flex='1'>Card Type</LabelCell>
                    <Cell flex='1'>{this.props.card.cardType?.name || this.props.cardTypeName || '-'}</Cell>
                  </Row>
                  {
                    !this.props.overlayId &&
                    <>
                      <Row>
                        <LabelCell flex='1'>Reviews</LabelCell>
                        <Cell flex='1'>{this.props.card.viewsCount ?? '-'}</Cell>
                      </Row>
                      <Row>
                        <LabelCell flex='1'>Consecutive Positive</LabelCell>
                        <Cell flex='1'>{this.props.card.consecutiveGoodScores ?? '-'}</Cell>
                      </Row>
                      <Row>
                        <LabelCell flex='1'>Created</LabelCell>
                        <Cell flex='1'>{this.props.card.createdAt?.fullTextFormattedDateWithYear || '-'}</Cell>
                      </Row>
                      <Row>
                        <LabelCell flex='1'>Due</LabelCell>
                        <Cell flex='1'>
                          {this.props.reviewAfterText}
                        </Cell>
                      </Row>
                    </>
                  }
                  <Row>
                    <LabelCell flex='1'>Difficulty</LabelCell>
                    <Cell flex='1'>
                      {
                        this.props.card.difficultyLabel &&
                        <StatusTag background={this.props.card.difficultyColor}>
                          {this.props.card.difficultyLabel}
                        </StatusTag>
                      }
                    </Cell>
                  </Row>
                  <Row>
                    <LabelCell flex='1'>Status</LabelCell>
                    <Cell flex='1'>
                      {
                        Boolean(this.props.card.retired) &&
                        <StatusTag background={Theme.purple}>
                          <i className='fa fa-archive' style={{fontSize: '11px'}} />
                          Retired
                        </StatusTag>
                      }
                      {
                        Boolean(!this.props.card.retired && this.props.isReviewPastDue) &&
                        <StatusTag background={Theme.orange}>
                          <i className='fa fa-clock-o' style={{fontSize: '11px'}} />
                          Past Due
                        </StatusTag>
                      }
                      {
                        Boolean(!this.props.card.retired && !this.props.isReviewPastDue) &&
                        <StatusTag background={Theme.green}>
                          <i className='fa fa-check' style={{fontSize: '11px'}} />
                          Caught Up
                        </StatusTag>
                      }
                    </Cell>
                  </Row>
                </Table>
                <NavList>
                  <NavItem onClick={() => this.showEditNoteModal = true}>
                    Edit Review Note
                    <i className='fa fa-angle-right' style={{color: '#ccc'}} />
                  </NavItem>
                  <NavItem onClick={() => this.showChangeCardTypeModal = true}>
                    Change Card Type
                    <i className='fa fa-angle-right' style={{color: '#ccc'}} />
                  </NavItem>
                  {
                    AppUI.reviewDeckOptions.filter(([id]) => String(id) !== String(this.props.card.deckId)).length > 0 &&
                    <NavItem
                      last
                      onClick={() => this.showMoveToDeckModal = true}
                    >
                      Move to Another Deck
                      <i className='fa fa-angle-right' style={{color: '#ccc'}} />
                    </NavItem>
                  }
                </NavList>
                <Buttons>
                  {
                    this.props.card.viewsCount > 0 &&
                    <Button
                      onClick={() => this.openConfirm('reset')}
                    >
                      Reset Review
                    </Button>
                  }
                  <Button
                    onClick={() => this.openConfirm('snooze')}
                  >
                    Snooze Review
                  </Button>
                  {
                    this.props.card &&
                    <Button
                      onClick={this.props.onRetireReview}
                      background={this.props.card.retired ? Theme.green : Theme.purple}
                      color='white'
                      disabled={this.props.retireReviewPending}
                    >
                      {this.props.card.retired ? 'Unretire Review' : 'Retire Review'}
                    </Button>
                  }
                  <Button
                    onClick={() => this.openConfirm('delete')}
                    background={Theme.red}
                    color='white'
                  >
                    Delete Review
                  </Button>
                </Buttons>
              </Module>
            </LeftColumn>
            <RightColumn inDeck={inDeck}>
              <Module inDeck={inDeck}>
                <InfoButton
                  inDeck={inDeck}
                  onClick={() => this.showExplainer = !this.showExplainer}
                >
                  <i className='fa fa-info-circle' />
                </InfoButton>
                <TriangleSection inDeck={inDeck}>
                  <SegmentedTriangle
                    fill1={this.reviewProgressFillForSegment(1)}
                    fill2={this.reviewProgressFillForSegment(2)}
                    fill3={this.reviewProgressFillForSegment(3)}
                    fill4={this.reviewProgressFillForSegment(4)}
                    fill5={this.reviewProgressFillForSegment(5)}
                    fill6={this.reviewProgressFillForSegment(6)}
                    fill7={this.reviewProgressFillForSegment(7)}
                    fill8={this.reviewProgressFillForSegment(8)}
                    fill9={this.reviewProgressFillForSegment(9)}
                    fill10={this.reviewProgressFillForSegment(10)}
                  />
                </TriangleSection>
                {
                  this.showExplainer &&
                    <Explainer>
                      <i className='fa fa-info-circle' style={{color: '#aaa', marginTop: '2px', flexShrink: 0}} />
                      <span>
                        {`The current review interval is ${this.props.currentRepetitionIntervalText}. Once the interval reaches ${AppUI.user.daysUntilReviewRetired}, the review can be retired. `}
                        <a
                          href='https://seedlang.com/help_center/topic/7d2bdc2a-34ec-414a-971c-afb00c9291a5/article/6864c661-5cd6-4769-8c1c-2e38e4df268a'
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          Learn more
                        </a>
                      </span>
                    </Explainer>
                  }
                {
                  this.props.overlayId &&
                  <Button
                    margin='8px 0 0'
                    background='#ececec'
                    color='#555'
                    fontSize='13px'
                    borderRadius='10px'
                    onClick={() => OverlayUI.addOverlay('reviewHistory', {
                      card: this.props.card,
                      cardId: this.props.card.id,
                      viewsCount: this.props.card.viewsCount,
                      creationDateText: this.props.card.createdAt?.formattedDateWithYear,
                    })}
                  >
                    See Review History
                  </Button>
                }
                {
                  !this.props.overlayId &&
                  <div style={{marginTop: inDeck ? '8px' : '20px'}}>
                    <ReviewHistoryTable
                      cardId={this.props.cardId}
                      testReviewRows={this.props.testReviewRows}
                      viewsCount={this.props.card.viewsCount}
                      creationDateText={this.props.card.createdAt?.formattedDateWithYear}
                      reviewsDueAtBeginningOfDayValue={this.props.intervalTesterReviewsDueAtBeginningOfDay}
                    />
                  </div>
                }
              </Module>
            </RightColumn>
          </TwoColumnLayout>
        </Wrapper>
      );
    } else {
      return <span />;
    }
  }
}

export default ReviewDetailsWrapper(ReviewDetails);
